import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, message, Space, Alert, Tooltip, Modal } from 'antd';
import { FileWordOutlined, CopyOutlined, FilePdfOutlined, CheckCircleOutlined, SyncOutlined, EditOutlined } from '@ant-design/icons';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import requirementService from '../services/requirementService';
import { extractErrorMessage } from '../utils/errorHandler';
import DocumentRequirementUploader from './DocumentRequirementUploader';
// @ts-ignore
import htmlDocx from 'html-docx-js/dist/html-docx';

// Import ReactQuill and its styles
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './quill-custom.css';

const { TextArea } = Input;

interface RequirementsEditorProps {
  projectId: string;
  projectName?: string;
  initialValue: string;
  onSuccess: (updatedText: string) => void;
  onRequestSync?: () => void;
  onEditWithPrompt?: () => void;
  requirementsTextLoading?: boolean;
  requirementDocUrl?: string;
  errorMessage?: string | null;
  onGenerateRequirements?: () => void;
  generateRequirementsLoading?: boolean;
  disableGenerateRequirements?: boolean;
  canEdit?: boolean;
  canCreate?: boolean;
  requirementsApproved?: boolean;
  onApprovalChange?: () => void;
}

const RequirementsEditor: React.FC<RequirementsEditorProps> = ({
  projectId,
  projectName,
  initialValue,
  onSuccess,
  onRequestSync,
  onEditWithPrompt,
  requirementsTextLoading = false,
  requirementDocUrl,
  errorMessage,
  onGenerateRequirements,
  generateRequirementsLoading = false,
  disableGenerateRequirements = false,
  canEdit = false,
  canCreate = false,
  requirementsApproved = false,
  onApprovalChange
}) => {
  // Helper function to convert plain text to HTML with proper formatting
  const convertToHtml = (text: string) => {
    if (!text) return '';
    
    // If it's already properly formatted HTML, return as is
    if (text.includes('<h1') || text.includes('<h2') || text.includes('<strong')) {
      return text;
    }
    
    // Convert markdown-like formatting to HTML
    let htmlContent = text;
    
    // Convert headers (# ## ### #### etc.) with custom color
    htmlContent = htmlContent.replace(/^#### (.*$)/gm, '<h4 style="color: rgb(0, 0, 0);">$1</h4>');
    htmlContent = htmlContent.replace(/^### (.*$)/gm, '<h3 style="color: rgb(21, 96, 130);">$1</h3>');
    htmlContent = htmlContent.replace(/^## (.*$)/gm, '<h2 style="color: rgb(21, 96, 130);">$1</h2>');
    htmlContent = htmlContent.replace(/^# (.*$)/gm, '<h1 style="color: rgb(21, 96, 130);">$1</h1>');
    
    // Convert bold text (**text** or **text**)
    htmlContent = htmlContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert numbered lists (1. 1.1 1.1.1 etc.) — handles optional trailing dot and leading whitespace
    htmlContent = htmlContent.replace(/^\s*(\d+(?:\.\d+)*\.?)\s+(.*$)/gm, '<p><strong>$1</strong> $2</p>');
    
    // Convert bullet points (- text)
    htmlContent = htmlContent.replace(/^-\s+(.*$)/gm, '<p>• $1</p>');
    
    // Split into paragraphs and format
    const lines = htmlContent.split('\n');
    let result = [];
    let currentParagraph = [];
    
    for (let line of lines) {
      line = line.trim();
      
      // If line is already HTML (starts with <), add it directly
      if (line.startsWith('<h') || line.startsWith('<p>')) {
        // Finish current paragraph first
        if (currentParagraph.length > 0) {
          result.push(`<p>${currentParagraph.join('<br>')}</p>`);
          currentParagraph = [];
        }
        result.push(line);
      }
      // Empty line - finish current paragraph
      else if (!line) {
        if (currentParagraph.length > 0) {
          result.push(`<p>${currentParagraph.join('<br>')}</p>`);
          currentParagraph = [];
        }
      }
      // Regular line - add to current paragraph
      else {
        currentParagraph.push(line);
      }
    }
    
    // Add any remaining paragraph
    if (currentParagraph.length > 0) {
      result.push(`<p>${currentParagraph.join('<br>')}</p>`);
    }
    
    return result.join('');
  };

  // Helper function to convert HTML back to markdown for saving
  const convertHtmlToMarkdown = (html: string): string => {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    let markdown = '';

    const processNode = (node: Node): string => {
      let text = '';

      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();

        switch (tagName) {
          case 'h1':
            text += `# ${element.textContent?.trim()}\n\n`;
            break;
          case 'h2':
            text += `## ${element.textContent?.trim()}\n\n`;
            break;
          case 'h3':
            text += `### ${element.textContent?.trim()}\n\n`;
            break;
          case 'h4':
            text += `#### ${element.textContent?.trim()}\n\n`;
            break;
          case 'h5':
          case 'h6':
            text += `${'#'.repeat(parseInt(tagName[1]))} ${element.textContent?.trim()}\n\n`;
            break;
          case 'p': {
            // Check if this is a numbered list pattern: <p><strong>1.1</strong> text</p>
            const firstChild = element.firstElementChild;
            if (firstChild?.tagName.toLowerCase() === 'strong') {
              const strongText = firstChild.textContent?.trim() || '';
              // Check if the strong text matches a numbered pattern like 1. or 1.1 or 1.1.1
              if (/^\d+(?:\.\d+)*\.?$/.test(strongText)) {
                let restText = '';
                let sibling = firstChild.nextSibling;
                while (sibling) {
                  if (sibling.nodeType === Node.TEXT_NODE) {
                    restText += sibling.textContent || '';
                  } else if (sibling.nodeType === Node.ELEMENT_NODE) {
                    restText += (sibling as Element).textContent || '';
                  }
                  sibling = sibling.nextSibling;
                }
                text += `${strongText} ${restText.trim()}\n`;
                break;
              }
            }

            // Regular paragraph - process children to handle inline formatting
            let pContent = '';
            element.childNodes.forEach(child => {
              if (child.nodeType === Node.TEXT_NODE) {
                pContent += child.textContent || '';
              } else if (child.nodeType === Node.ELEMENT_NODE) {
                const childEl = child as Element;
                const childTag = childEl.tagName.toLowerCase();
                if (childTag === 'strong' || childTag === 'b') {
                  pContent += `**${childEl.textContent?.trim()}**`;
                } else if (childTag === 'em' || childTag === 'i') {
                  pContent += `*${childEl.textContent?.trim()}*`;
                } else if (childTag === 'br') {
                  pContent += '\n';
                } else {
                  pContent += childEl.textContent || '';
                }
              }
            });

            // Check if it's a bullet point (• text)
            if (pContent.trim().startsWith('•')) {
              text += `- ${pContent.trim().substring(1).trim()}\n`;
            } else {
              text += `${pContent.trim()}\n\n`;
            }
            break;
          }
          case 'ul':
            Array.from(element.children).forEach(li => {
              text += `- ${li.textContent?.trim()}\n`;
            });
            text += '\n';
            break;
          case 'ol':
            Array.from(element.children).forEach((li, index) => {
              text += `${index + 1}. ${li.textContent?.trim()}\n`;
            });
            text += '\n';
            break;
          case 'br':
            text += '\n';
            break;
          case 'blockquote': {
            const lines = (element.textContent?.trim() || '').split('\n');
            lines.forEach(line => {
              if (line.trim()) text += `> ${line.trim()}\n`;
            });
            text += '\n';
            break;
          }
          case 'pre': {
            const preText = element.textContent?.trim() || '';
            text += `\`\`\`\n${preText}\n\`\`\`\n\n`;
            break;
          }
          default:
            element.childNodes.forEach(child => {
              text += processNode(child);
            });
            break;
        }
      }

      return text;
    };

    tempDiv.childNodes.forEach(node => {
      markdown += processNode(node);
    });

    return markdown.replace(/\n{3,}/g, '\n\n').trim();
  };

  const [value, setValue] = useState(convertToHtml(initialValue));
  const [loading, setLoading] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const quillRef = useRef<ReactQuill>(null); // ReactQuill ref

  // Helper function to check if content has meaningful text
  const hasValidContent = (content: string) => {
    if (!content || !content.trim()) return false;
    
    // Extract text content from HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
    
    // Check if we have meaningful content (more than just whitespace/empty tags)
    const isValid = textContent.length > 0 && textContent !== '';
    
    return isValid;
  };

  // Store the original converted value to compare against
  const [originalValue, setOriginalValue] = React.useState<string>('');

  // Check if content has changed from original
  const hasContentChanged = React.useMemo(() => {
    // Simple check: if no original value is set yet, consider no change
    if (!originalValue) {
      console.log('No original value set yet - no change');
      return false;
    }

    // Extract plain text from both HTML values for comparison
    const getTextContent = (html: string) => {
      if (!html) return '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return (tempDiv.textContent || tempDiv.innerText || '').trim();
    };

    const originalText = getTextContent(originalValue);
    const currentText = getTextContent(value);

    const hasChanged = originalText !== currentText;

    return hasChanged;
  }, [originalValue, value]);

  // Update value when initialValue changes
  React.useEffect(() => {
    const convertedValue = convertToHtml(initialValue);
    setValue(convertedValue);
  }, [initialValue]);

  // Set original value after Quill is initialized and content is set
  React.useEffect(() => {
    if (quillRef.current && value) {
      // Wait for Quill to process the content properly
      const timer = setTimeout(() => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const quillContent = quill.root.innerHTML;
          // Only set original value if it hasn't been set yet or if it's significantly different
          if (!originalValue || Math.abs(quillContent.length - originalValue.length) > 10) {
            setOriginalValue(quillContent);
          }
        }
      }, 1000); // Give Quill more time to initialize

      return () => clearTimeout(timer);
    }
  }, [value, quillRef.current]); // Depend on both value and quillRef

  // ReactQuill configuration
  // Quill editor modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['blockquote', 'code-block'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  // Quill editor formats
  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'align', 'link', 'blockquote', 
    'code-block', 'color', 'background'
  ];

  const handleSave = async (shouldSync: boolean = false) => {
    setLoading(true);
    setSyncModalVisible(false);
    try {
      // Convert HTML back to markdown before saving so it renders properly on reload
      const markdownContent = convertHtmlToMarkdown(value);
      console.log('Saving requirements (markdown):', markdownContent.substring(0, 100));
      const response = await requirementService.updateRequirements(projectId, markdownContent);
      if (response.status === 'success') {
        message.success('Requirements updated successfully');
        // Update the original value to current value after successful save
        // Get the actual content from Quill to ensure consistency
        if (quillRef.current) {
          const quill = quillRef.current.getEditor();
          const quillContent = quill.root.innerHTML;
          setOriginalValue(quillContent);
        } else {
          setOriginalValue(value);
        }
        onSuccess(markdownContent);
        // Trigger sync if user chose Yes
        if (shouldSync && onRequestSync) {
          onRequestSync();
        }
      } else {
        const errorMsg = response?.message || 'Failed to update requirements. No valid response received.';
        message.error(errorMsg);
      }
    } catch (err) {
      console.error('Save error:', err);
      const errorMessage = extractErrorMessage(err);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show the sync choice dialog before saving
  const handleUpdateClick = () => {
    setSyncModalVisible(true);
  };

  const handleCopyContent = async () => {
    try {
      // Copy both HTML and plain text to clipboard for better compatibility
      const htmlContent = value;
      
      // Create a more structured plain text version that preserves formatting
      const convertHtmlToFormattedText = (html: string) => {
        if (!html) return '';
        
        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        let formattedText = '';
        
        // Process each child element
        const processNode = (node: Node, level: number = 0): string => {
          let text = '';
          const indent = '  '.repeat(level); // Indentation for nested elements
          
          if (node.nodeType === Node.TEXT_NODE) {
            // Text node - return the text content
            const textContent = node.textContent?.trim() || '';
            return textContent;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            const tagName = element.tagName.toLowerCase();
            
            switch (tagName) {
              case 'h1':
              case 'h2':
              case 'h3':
              case 'h4':
              case 'h5':
              case 'h6':
                // Headers with extra spacing and formatting
                const headerLevel = parseInt(tagName[1]);
                const headerText = element.textContent?.trim() || '';
                const headerPrefix = '='.repeat(Math.max(1, 7 - headerLevel));
                text += `\n\n${headerPrefix} ${headerText} ${headerPrefix}\n\n`;
                break;
                
              case 'p':
                // Paragraphs with line breaks
                const pText = Array.from(element.childNodes)
                  .map(child => processNode(child, level))
                  .join('')
                  .trim();
                if (pText) {
                  text += `${pText}\n\n`;
                }
                break;
                
              case 'br':
                // Line breaks
                text += '\n';
                break;
                
              case 'strong':
              case 'b':
                // Bold text with markers
                const boldText = element.textContent?.trim() || '';
                text += `**${boldText}**`;
                break;
                
              case 'em':
              case 'i':
                // Italic text with markers
                const italicText = element.textContent?.trim() || '';
                text += `*${italicText}*`;
                break;
                
              case 'ul':
                // Unordered lists
                text += '\n';
                Array.from(element.children).forEach(li => {
                  const liText = li.textContent?.trim() || '';
                  if (liText) {
                    text += `${indent}• ${liText}\n`;
                  }
                });
                text += '\n';
                break;
                
              case 'ol':
                // Ordered lists
                text += '\n';
                Array.from(element.children).forEach((li, index) => {
                  const liText = li.textContent?.trim() || '';
                  if (liText) {
                    text += `${indent}${index + 1}. ${liText}\n`;
                  }
                });
                text += '\n';
                break;
                
              case 'li':
                // List items (handled by parent ul/ol)
                break;
                
              case 'blockquote':
                // Block quotes with indentation
                const quoteText = element.textContent?.trim() || '';
                const quoteLines = quoteText.split('\n');
                quoteLines.forEach(line => {
                  if (line.trim()) {
                    text += `${indent}> ${line.trim()}\n`;
                  }
                });
                text += '\n';
                break;
                
              case 'code':
                // Inline code
                const codeText = element.textContent?.trim() || '';
                text += `\`${codeText}\``;
                break;
                
              case 'pre':
                // Code blocks
                const preText = element.textContent?.trim() || '';
                text += `\n\`\`\`\n${preText}\n\`\`\`\n\n`;
                break;
                
              case 'a':
                // Links with URL
                const linkText = element.textContent?.trim() || '';
                const href = element.getAttribute('href');
                if (href) {
                  text += `${linkText} (${href})`;
                } else {
                  text += linkText;
                }
                break;
                
              default:
                // For other elements, process children recursively
                Array.from(element.childNodes).forEach(child => {
                  text += processNode(child, level + 1);
                });
                break;
            }
          }
          
          return text;
        };
        
        // Process all child nodes
        Array.from(tempDiv.childNodes).forEach(node => {
          formattedText += processNode(node);
        });
        
        // Clean up extra whitespace while preserving intentional spacing
        return formattedText
          .replace(/\n{3,}/g, '\n\n')  // Max 2 consecutive newlines
          .replace(/^\n+/, '')         // Remove leading newlines
          .replace(/\n+$/, '\n')       // Single trailing newline
          .trim();
      };
      
      const formattedPlainText = convertHtmlToFormattedText(htmlContent);
      
      // Use the modern Clipboard API to copy both formats
      if (navigator.clipboard && window.ClipboardItem) {
        // Create clipboard item with both HTML and formatted plain text
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([formattedPlainText], { type: 'text/plain' })
        });
        
        await navigator.clipboard.write([clipboardItem]);
        message.success('Content copied to clipboard');
      } else {
        // Fallback to formatted plain text if ClipboardItem is not supported
        await navigator.clipboard.writeText(formattedPlainText);
        message.success('Content copied to clipboard');
      }
    } catch (err) {
      console.error('Failed to copy content: ', err);
      
      // Final fallback: try to copy basic plain text
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = value;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';
        await navigator.clipboard.writeText(plainText);
        message.success('Content copied to clipboard');
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
        message.error('Failed to copy content');
      }
    }
  };


  const handleDownloadDocxWithMarked = async () => {
    if (!hasValidContent(value)) {
      message.warning('No content to download');
      return;
    }

    try {
      // ReactQuill already provides HTML content, so we use it directly
      let htmlContent = value;

      // Ensure all headers have the correct color - more comprehensive approach
      // First, handle headers that already have style attributes but no color
      htmlContent = htmlContent.replace(/<h([1-6])\s+style="([^"]*)"(?![^"]*color[^"]*:)/g, '<h$1 style="$2; color: rgb(21, 96, 130);"');
      
      // Then, handle headers without any style attribute
      htmlContent = htmlContent.replace(/<h([1-6])(?!\s+style)/g, '<h$1 style="color: rgb(21, 96, 130);"');
      
      // Finally, handle headers that have color but we want to override it
      htmlContent = htmlContent.replace(/(<h[1-6][^>]*style="[^"]*color:\s*)[^;"]*(;?[^"]*")/g, '$1rgb(21, 96, 130)$2');

      // Wrap in full HTML structure with Calibri font and custom styling
      const fullHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>BRD Document</title>
                <style>
                    body {
                        font-family: 'Calibri', 'Arial', sans-serif;
                        font-size: 11pt;
                        line-height: 1.15;
                        color: #000000;
                        margin: 0;
                        padding: 0;
                    }
                    h1, h2, h3, h4, h5, h6 {
                        font-family: 'Calibri', 'Arial', sans-serif;
                        color: rgb(21, 96, 130) !important;
                        font-weight: bold;
                        margin-top: 12pt;
                        margin-bottom: 6pt;
                    }
                    h1 { font-size: 20pt; }
                    h2 { font-size: 16pt; }
                    h3 { font-size: 14pt; }
                    h4 { font-size: 12pt; }
                    h5 { font-size: 11pt; }
                    h6 { font-size: 10pt; }
                    p {
                        font-family: 'Calibri', 'Arial', sans-serif;
                        font-size: 11pt;
                        margin-top: 0pt;
                        margin-bottom: 8pt;
                        line-height: 1.15;
                    }
                    ul, ol {
                        font-family: 'Calibri', 'Arial', sans-serif;
                        font-size: 11pt;
                        margin-top: 0pt;
                        margin-bottom: 8pt;
                        padding-left: 18pt;
                    }
                    li {
                        margin-bottom: 2pt;
                        line-height: 1.15;
                    }
                    strong, b {
                        font-weight: bold;
                        color: rgb(21, 96, 130);
                    }
                    em, i {
                        font-style: italic;
                        color: rgb(21, 96, 130);
                    }
                    blockquote {
                        font-family: 'Calibri', 'Arial', sans-serif;
                        font-size: 11pt;
                        margin: 8pt 0;
                        padding: 6pt 12pt;
                        border-left: 3pt solid rgb(21, 96, 130);
                        background-color: #f9f9f9;
                        font-style: italic;
                    }
                    code {
                        font-family: 'Consolas', 'Courier New', monospace;
                        font-size: 10pt;
                        background-color: #f5f5f5;
                        padding: 1pt 3pt;
                        border-radius: 2pt;
                    }
                    pre {
                        font-family: 'Consolas', 'Courier New', monospace;
                        font-size: 10pt;
                        background-color: #f5f5f5;
                        padding: 8pt;
                        margin: 8pt 0;
                        border-radius: 3pt;
                        white-space: pre-wrap;
                    }
                    a {
                        color: rgb(21, 96, 130);
                        text-decoration: underline;
                    }
                    table {
                        border-collapse: collapse;
                        width: 100%;
                        margin: 8pt 0;
                        font-family: 'Calibri', 'Arial', sans-serif;
                        font-size: 11pt;
                    }
                    th, td {
                        border: 1pt solid #cccccc;
                        padding: 4pt 6pt;
                        text-align: left;
                        vertical-align: top;
                    }
                    th {
                        background-color: #f0f0f0;
                        font-weight: bold;
                        color: rgb(21, 96, 130);
                    }
                </style>
            </head>
            <body>${htmlContent}</body>
            </html>`;

      // Generate and download the document
      const blob = htmlDocx.asBlob(fullHTML);
      // Create a clean filename using project name or fallback to project ID
      const filename = projectName 
        ? `${projectName.replace(/[^a-zA-Z0-9\s-_]/g, '').trim().replace(/\s+/g, '_')}.docx`
        : `requirements-${projectId}.docx`;
      saveAs(blob, filename);

      message.success('Requirements downloaded successfully');
    } catch (error) {
      console.error('Error generating requirements document:', error);
      message.error('Failed to generate requirements document');
    }
  };

  const handleDownloadPdf = async () => {
    if (!hasValidContent(value)) {
      message.warning('No content to download');
      return;
    }

    try {
      // ReactQuill already provides HTML content, so we use it directly
      const htmlContent = value;

      // Create a temporary container for HTML content
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.top = '-9999px';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '794px'; // A4 width in pixels at 96 DPI
      tempContainer.style.padding = '40px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.fontSize = '14px';
      tempContainer.style.lineHeight = '1.6';
      tempContainer.style.color = '#333';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.boxSizing = 'border-box';

      // Add styles for better PDF formatting
      tempContainer.innerHTML = `
        <style>
          * { box-sizing: border-box; }
          h1, h2, h3, h4, h5, h6 { 
            color: #333; 
            margin-top: 24px; 
            margin-bottom: 12px; 
            font-weight: bold;
          }
          h1 { font-size: 28px; }
          h2 { font-size: 24px; }
          h3 { font-size: 20px; }
          h4 { font-size: 18px; }
          p { 
            margin-bottom: 16px; 
            text-align: justify;
          }
          ul, ol { 
            margin-bottom: 16px; 
            padding-left: 24px; 
          }
          li { margin-bottom: 8px; }
          code { 
            background-color: #f8f9fa; 
            padding: 4px 8px; 
            font-family: 'Courier New', monospace; 
            border-radius: 4px;
            font-size: 12px;
          }
          pre {
            background-color: #f8f9fa;
            padding: 16px;
            border-radius: 8px;
            font-family: 'Courier New', monospace;
            margin: 16px 0;
            overflow-x: auto;
            font-size: 12px;
          }
          blockquote { 
            border-left: 4px solid #007bff; 
            margin: 20px 0; 
            padding: 16px 20px; 
            font-style: italic;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
          strong { font-weight: 600; }
          em { font-style: italic; }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 16px 0;
          }
          th, td {
            border: 1px solid #dee2e6;
            padding: 12px;
            text-align: left;
          }
          th {
            background-color: #f8f9fa;
            font-weight: 600;
          }
          img {
            max-width: 100%;
            height: auto;
          }
        </style>
        ${htmlContent}
      `;

      // Append to body to render
      document.body.appendChild(tempContainer);

      // Wait for content to render properly
      await new Promise(resolve => setTimeout(resolve, 200));

      // Generate canvas from HTML content
      const canvas = await html2canvas(tempContainer, {
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Create PDF with proper multi-page handling
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margins = 20; // 20mm margins
      const contentWidth = pdfWidth - (margins * 2);
      const contentHeight = pdfHeight - (margins * 2);

      // Convert canvas to image data
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Calculate how the image should fit in the PDF
      const ratio = contentWidth / (imgWidth * 0.264583); // Convert px to mm
      const scaledHeight = (imgHeight * 0.264583) * ratio;

      // Calculate how many pages we need
      const totalPages = Math.ceil(scaledHeight / contentHeight);

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        // Calculate the portion of the image for this page
        const sourceY = (page * contentHeight / ratio) / 0.264583; // Convert back to px
        const sourceHeight = Math.min(
          (contentHeight / ratio) / 0.264583, // Max height for this page in px
          imgHeight - sourceY // Remaining image height
        );

        // Only add image if there's content for this page
        if (sourceHeight > 0) {
          // Create a temporary canvas for this page's content
          const pageCanvas = document.createElement('canvas');
          const pageCtx = pageCanvas.getContext('2d');
          pageCanvas.width = imgWidth;
          pageCanvas.height = sourceHeight;

          // Draw the portion of the original image for this page
          const img = new Image();
          await new Promise((resolve) => {
            img.onload = () => {
              pageCtx!.drawImage(img, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
              resolve(null);
            };
            img.src = imgData;
          });

          const pageImgData = pageCanvas.toDataURL('image/png');
          const actualHeight = Math.min(scaledHeight - (page * contentHeight), contentHeight);

          pdf.addImage(pageImgData, 'PNG', margins, margins, contentWidth, actualHeight);
        }
      }

      // Download PDF
      pdf.save(`requirements-pdf-${projectId}-${new Date().toISOString().split('T')[0]}.pdf`);

      message.success(`Requirements PDF downloaded successfully (${totalPages} pages)`);
    } catch (error) {
      console.error('Error generating PDF document:', error);
      
      // Clean up temporary container if it still exists
      const existingContainer = document.querySelector('div[style*="-9999px"]');
      if (existingContainer) {
        document.body.removeChild(existingContainer);
      }
      
      message.error('Failed to generate PDF document');
    }
  };

  const [approveLoading, setApproveLoading] = useState(false);

  const handleApproveRequirements = async () => {
    setApproveLoading(true);
    try {
      const response = await requirementService.approveRequirements(projectId, 'requirements');
      if (response.status === 'success') {
        message.success('Requirements approved successfully');
        if (onApprovalChange) {
          onApprovalChange();
        }
      } else {
        const errorMsg = response?.message || 'Failed to approve requirements.';
        message.error(errorMsg);
      }
    } catch (err) {
      console.error('Approve error:', err);
      const errorMessage = extractErrorMessage(err);
      message.error(errorMessage);
    } finally {
      setApproveLoading(false);
    }
  };

  return (
    <div>
      {/* Error Display */}
      {errorMessage && (
        <Alert message={errorMessage} type="error" showIcon style={{ marginBottom: 16 }} />
      )}

      {/* Conditional Layout based on requirement document existence */}
      {requirementDocUrl ? (
        <div>
          {/* Requirements Document Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '16px', color: '#111827', fontSize: '18px', fontWeight: 600 }}>
              Requirements Document
            </h3>
            <Button
              type="primary"
              href={requirementDocUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: '#dc3545',
                borderColor: '#dc3545',
                marginBottom: '16px'
              }}
              icon={<span>📄</span>}
            >
              View Requirements Document
            </Button>
          </div>

          {/* Update Requirements Text Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '16px', color: '#111827', fontSize: '18px', fontWeight: 600 }}>
              Update Requirements Text
            </h3>

            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              background: '#ffffff'
            }}>
              {/* ReactQuill Editor */}
                <div style={{ marginBottom: '12px' }}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={value}
                  onChange={(content) => {
                    setValue(content);
                  }}
                  readOnly={requirementsTextLoading || !canEdit}
                  placeholder={requirementsTextLoading ? "Loading requirements..." : (canEdit ? "Enter your requirements here..." : "View mode - no edit permission")}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{
                    backgroundColor: (requirementsTextLoading || !canEdit) ? '#f5f5f5' : '#ffffff',
                    height: '300px'
                  }}
                />
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                flexWrap: 'wrap',
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                marginTop: '50px',
                padding: '8px 0',
                minHeight: '48px',
                borderTop: '1px solid #f0f0f0'
              }}>
                {canEdit && (
                  <Button
                    type={hasContentChanged && hasValidContent(value) && !requirementsTextLoading ? 'primary' : 'default'}
                    onClick={handleUpdateClick}
                    loading={loading}
                    disabled={!hasContentChanged || requirementsTextLoading || !hasValidContent(value)}
                    style={{
                      minWidth: '160px',
                      height: '40px',
                      borderRadius: '6px',
                      fontWeight: '500',
                      fontSize: '14px',
                      boxShadow: hasContentChanged && hasValidContent(value) && !requirementsTextLoading
                        ? '0 2px 4px rgba(220, 53, 69, 0.2)' 
                        : 'none',
                      ...(hasContentChanged && hasValidContent(value) && !requirementsTextLoading ? {
                        backgroundColor: '#dc3545',
                        borderColor: '#dc3545',
                        color: '#ffffff'
                      } : {
                        backgroundColor: '#f8f9fa',
                        borderColor: '#dee2e6',
                        color: '#6c757d',
                        cursor: 'not-allowed'
                      })
                    }}
                  >
                    Update Requirements
                  </Button>
                )}
                <Button
                  type="default"
                  icon={<CopyOutlined />}
                  onClick={handleCopyContent}
                  disabled={!hasValidContent(value)}
                  style={{
                    minWidth: '130px',
                    height: '40px',
                    borderRadius: '6px',
                    fontWeight: '500',
                    fontSize: '14px',
                    backgroundColor: hasValidContent(value) ? '#f8f9fa' : '#f8f9fa',
                    borderColor: hasValidContent(value) ? '#6c757d' : '#dee2e6',
                    color: hasValidContent(value) ? '#495057' : '#adb5bd',
                    cursor: hasValidContent(value) ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  Copy Content
                </Button>
                <Button
                  type="default"
                  icon={<FileWordOutlined />}
                  onClick={handleDownloadDocxWithMarked}
                  disabled={!hasValidContent(value)}
                  style={{
                    minWidth: '140px',
                    height: '40px',
                    borderRadius: '6px',
                    fontWeight: '500',
                    fontSize: '14px',
                    backgroundColor: hasValidContent(value) ? '#e3f2fd' : '#f8f9fa',
                    borderColor: hasValidContent(value) ? '#1976d2' : '#dee2e6',
                    color: hasValidContent(value) ? '#1976d2' : '#adb5bd',
                    cursor: hasValidContent(value) ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  Download Word
                </Button>
                {canEdit && !requirementsApproved && hasValidContent(value) && (
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleApproveRequirements}
                    loading={approveLoading}
                    disabled={approveLoading}
                    style={{
                      minWidth: '140px',
                      height: '40px',
                      borderRadius: '6px',
                      fontWeight: '500',
                      fontSize: '14px',
                      backgroundColor: '#28a745',
                      borderColor: '#28a745',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
                    }}
                  >
                    Approve
                  </Button>
                )}
                {requirementsApproved && (
                  <Button
                    type="default"
                    icon={<CheckCircleOutlined />}
                    disabled
                    style={{
                      minWidth: '140px',
                      height: '40px',
                      borderRadius: '6px',
                      fontWeight: '500',
                      fontSize: '14px',
                      backgroundColor: '#d4edda',
                      borderColor: '#c3e6cb',
                      color: '#155724',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      cursor: 'not-allowed'
                    }}
                  >
                    Approved
                  </Button>
                )}
                {canEdit && onEditWithPrompt && hasValidContent(value) && (
                  <Button
                    type="default"
                    icon={<EditOutlined />}
                    onClick={onEditWithPrompt}
                    style={{
                      minWidth: '160px',
                      height: '40px',
                      borderRadius: '6px',
                      fontWeight: '500',
                      fontSize: '14px',
                      backgroundColor: '#f8f9fa',
                      borderColor: '#dc3545',
                      color: '#dc3545',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit with Prompt
                  </Button>
                )}
                {/* <Button
                  type="default"
                  icon={<FilePdfOutlined />}
                  onClick={handleDownloadPdf}
                  disabled={!value.trim()}
                  style={{
                    backgroundColor: value.trim() ? '#fff7ed' : '#f9f9f9',
                    borderColor: value.trim() ? '#fed7aa' : '#e5e5e5',
                    color: value.trim() ? '#ea580c' : '#9ca3af',
                    cursor: value.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  Download PDF
                </Button> */}
              </div>
            </div>
          </div>

          {/* Document Requirement Uploader */}
          <DocumentRequirementUploader
            projectId={projectId}
            onSuccess={() => {
              // Don't reload — the sync modal will handle navigation
            }}
            onRequestSync={onRequestSync}
          />

          {/* Sync Confirmation Modal - shown before save */}
          <Modal
            title="Sync Downstream Stages?"
            open={syncModalVisible}
            onCancel={() => setSyncModalVisible(false)}
            footer={[
              <Button key="no" onClick={() => handleSave(false)}>
                No
              </Button>,
              <Button
                key="yes"
                type="primary"
                icon={<SyncOutlined />}
                onClick={() => handleSave(true)}
                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
              >
                Yes
              </Button>
            ]}
          >
            <p style={{ margin: '16px 0', color: '#495057' }}>
              Do you want to sync all downstream stages with the updated requirements?
            </p>
            <p style={{ margin: '0', color: '#6c757d', fontSize: '13px' }}>
              <strong>Yes</strong> — Save requirements and sync all downstream stages.<br />
              <strong>No</strong> — Only save requirements.
            </p>
          </Modal>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <Space direction="vertical" size={16} style={{ alignItems: 'center' }}>
            <Alert
              message="No Requirements created yet"
              type="info"
              showIcon
              style={{
                marginBottom: 0,
                textAlign: 'center'
              }}
            />
            {onGenerateRequirements && canCreate && (
              <Tooltip title={disableGenerateRequirements ? 'Please extract the Figma design first' : undefined}>
                <Button
                  type="primary"
                  onClick={onGenerateRequirements}
                  loading={generateRequirementsLoading}
                  disabled={disableGenerateRequirements}
                  size="large"
                  style={{
                    backgroundColor: disableGenerateRequirements ? undefined : '#dc3545',
                    borderColor: disableGenerateRequirements ? undefined : '#dc3545'
                  }}
                >
                  Generate Requirements
                </Button>
              </Tooltip>
            )}
          </Space>
        </div>
      )}
    </div>
  );
};

export default RequirementsEditor;